// src/utils/taskImages.js
//
// Everything the task-screenshot feature needs from the network and from the
// file the user picked, in one place — so any screen that wants to show or
// change a task's images imports this rather than reimplementing the shrinking
// rules and getting different ones.

import api from "./api";
import { apiPath } from "./apiPath";

/* Two per task, matching sp_QueryImage_Save. Repeated here so the form can
   grey out the button rather than let somebody choose a third file and only
   then be told; the procedure remains the one that actually enforces it. */
export const MAX_TASK_IMAGES = 2;

/* What the server will accept — kept in step with ALLOWED in
   taskImage.controller. */
export const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];

/* The longest edge an image is stored at, and the JPEG quality used when one
   has to be re-encoded.
 *
 * A phone screenshot is 3-4 MB and 3000px wide; it is looked at in a 260px
 * box. Sending the original would push the request past the body limit, store
 * megabytes per row, and look no better. 1600px is generous for reading a
 * screenshot of a screen, and the resize happens before the upload rather than
 * on the server, so the network carries the small version too. */
const MAX_EDGE = 1600;
const QUALITY = 0.85;

const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("That file could not be read."));
    reader.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("That file is not an image."));
    img.src = src;
  });

/**
 * A File, ready to send: shrunk if it is large, left alone if it is not.
 *
 * Returns { dataUrl, fileName, contentType, bytes }.
 */
export const prepareImage = async (file) => {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    throw new Error("Only PNG, JPG, GIF and WEBP images can be attached.");
  }

  const original = await readAsDataUrl(file);

  /* GIFs are passed through untouched: drawing one to a canvas keeps the first
     frame and silently throws the animation away, and an animated GIF is
     usually attached BECAUSE it moves. */
  if (file.type === "image/gif") {
    return {
      dataUrl: original,
      fileName: file.name,
      contentType: file.type,
      bytes: file.size,
    };
  }

  const img = await loadImage(original);
  const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));

  /* Already small enough — re-encoding would only lose quality to save
     nothing. */
  if (scale === 1 && file.size <= 1_500_000) {
    return {
      dataUrl: original,
      fileName: file.name,
      contentType: file.type,
      bytes: file.size,
    };
  }

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  /* PNG for anything with transparency, JPEG otherwise. A screenshot re-encoded
     as JPEG at 85 is a third the size and indistinguishable; a logo with a
     transparent background re-encoded as JPEG grows a black box. */
  const keepPng = file.type === "image/png" || file.type === "image/webp";
  const outType = keepPng ? "image/png" : "image/jpeg";
  const dataUrl = canvas.toDataURL(outType, QUALITY);

  return {
    dataUrl,
    fileName: file.name,
    contentType: outType,
    /* base64 is 4 bytes per 3 — near enough for a size to show the user. */
    bytes: Math.round((dataUrl.length - dataUrl.indexOf(",") - 1) * 0.75),
  };
};

/* successHandler wraps once and axios wraps again, and which level carries the
   rows has differed by endpoint before. */
const rowsOf = (payload) => {
  const body = payload?.data ?? payload;
  const inner = body?.data ?? body;
  return Array.isArray(inner) ? inner : [];
};

export const listTaskImages = async (queryCode) => {
  const res = await api.get(apiPath.taskImage.list, { params: { queryCode } });
  return rowsOf(res.data);
};

export const uploadTaskImage = async (queryCode, prepared) => {
  const res = await api.post(apiPath.taskImage.add, {
    queryCode,
    fileName: prepared.fileName,
    contentType: prepared.contentType,
    image: prepared.dataUrl,
  });
  return rowsOf(res.data);
};

export const removeTaskImage = async (queryImageCode) => {
  const res = await api.delete(apiPath.taskImage.remove(queryImageCode));
  return rowsOf(res.data);
};

/**
 * One image's bytes, as an object URL.
 *
 * Fetched through the shared axios instance rather than pointed at from an
 * <img src>, because the route is authenticated and an <img> tag cannot carry
 * an Authorization header. The caller owns the URL and must revoke it.
 */
export const fetchTaskImageUrl = async (queryImageCode) => {
  const res = await api.get(apiPath.taskImage.file(queryImageCode), {
    responseType: "blob",
  });
  return URL.createObjectURL(res.data);
};

/**
 * Apply a form's screenshot changes to a task, in one go, after the task
 * itself has been saved.
 *
 * The form stages everything: TaskImages never writes on its own, because
 * Cancel has to mean cancel and an image deleted the moment its X was clicked
 * survived nobody's second thoughts. This is where the staged decisions
 * finally reach the server.
 *
 * Removals run BEFORE additions, and one at a time. Both matter:
 *
 *   sp_QueryImage_Save counts the images already attached under a lock and
 *   refuses the third. Somebody who drops one screenshot and picks another has
 *   made a swap, and sending the addition first turns that swap into "both
 *   slots are full". Deleting first frees the slot the new one is meant for.
 *
 *   Sequential, not Promise.all, for the same lock: two uploads racing would
 *   each be told there was room for a second.
 *
 * Returns the task's images as the last call left them, so a caller that keeps
 * a list on screen does not need to fetch it again.
 */
export const commitTaskImages = async (queryCode, { removed = [], added = [] } = {}) => {
  if (!queryCode) return [];

  let rows = null;
  for (const code of removed) {
    // eslint-disable-next-line no-await-in-loop
    rows = await removeTaskImage(code);
  }
  for (const item of added) {
    // eslint-disable-next-line no-await-in-loop
    rows = await uploadTaskImage(queryCode, item);
  }
  return rows ?? [];
};
